'use client'

import { Fragment, useState } from 'react'
import { BackgroundLayers, RichText } from '@/components/ui'
import FadeUp from '@/components/ui/FadeUp'
import { getStrapiMedia } from '@/lib/strapi/client'
import type { TeaserFaqGroupType } from '@/lib/types/teaser'
import { isLayoutB } from '@/lib/utils/section-styles'

type Props = {
  data: TeaserFaqGroupType[]
}

type FaqItem = NonNullable<NonNullable<TeaserFaqGroupType['faqItems']>[number]>

function getHeaderAlignmentClass(
  alignment: 'Left' | 'Center' | 'Right' | null | undefined,
) {
  switch (alignment) {
    case 'Left':
      return 'items-start text-left'
    case 'Right':
      return 'items-end text-right'
    case 'Center':
    default:
      return 'items-center text-center'
  }
}

function PlusMinusIcon({ open, className }: { open: boolean; className?: string }) {
  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center ${className ?? ''}`}
      aria-hidden="true"
    >
      {/* Plus: 14×16 mobile, 14×14 desktop */}
      <svg
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`h-4 w-3.5 transition-opacity duration-300 md:size-3.5 ${
          open ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <path d="M6 6V0H8V6H14V8H8V14H6V8H0V6H6Z" fill="currentColor" />
      </svg>
      {/* Minus: 16×2 mobile, 14×2 desktop */}
      <svg
        viewBox="0 0 14 2"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`absolute left-1/2 top-1/2 h-0.5 w-4 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-300 md:w-3.5 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <path d="M0 0V2H14V0H0Z" fill="currentColor" />
      </svg>
    </span>
  )
}

function ChevronIcon({ open, className }: { open: boolean; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={`${className ?? ''} transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
    >
      <path
        d="M3 6L8 11L13 6"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function FaqAccordionItem({
  item,
  open,
  onToggle,
  delay,
  className,
}: {
  item: FaqItem
  open: boolean
  onToggle: () => void
  delay: number
  className?: string
}) {
  const answerId = `teaser-faq-answer-${item.id}`
  const hasAnswer =
    Array.isArray(item.description) && item.description.length > 0

  return (
    <FadeUp
      data-fig={`teaser-faq-item-${item.id}`}
      className={`w-full ${className ?? ''}`}
      delay={delay}
      durationMs={1200}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center justify-between gap-2 text-left min-h-11 md:min-h-6 md:justify-center focus-visible:outline-none focus-visible:underline"
        aria-expanded={open}
        aria-controls={answerId}
      >
        <span className="min-w-0 flex-1 font-body text-base font-semibold leading-[150%] text-card-heading md:leading-5">
          {item.heading}
        </span>
        <PlusMinusIcon
          open={open}
          className="h-4 w-4 text-card-heading md:size-3.5"
        />
      </button>

      <div
        id={answerId}
        className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        inert={!open ? true : undefined}
        aria-hidden={open ? undefined : true}
      >
        <div className="min-h-0 overflow-hidden">
          {hasAnswer ? (
            /* Q→A gap: 10px mobile / 16px desktop (only when open) */
            <div className={open ? 'pt-2.5 md:pt-4' : undefined}>
              <RichText
                content={item.description}
                className="m-0 flex flex-col gap-0 font-body text-sm font-normal leading-[150%] text-text-primary md:text-base md:leading-[26px] [&_p]:m-0"
              />
            </div>
          ) : null}
        </div>
      </div>
    </FadeUp>
  )
}

function FaqCardItem({
  item,
  open,
  onToggle,
  delay,
}: {
  item: FaqItem
  open: boolean
  onToggle: () => void
  delay: number
}) {
  const answerId = `teaser-faq-card-answer-${item.id}`

  return (
    <FadeUp
      data-fig={`teaser-faq-item-${item.id}`}
      className="w-full overflow-hidden rounded-xl border border-border bg-surface-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] md:rounded-2xl"
      delay={delay}
      durationMs={1200}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full cursor-pointer items-center justify-between gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset ${
          open ? 'px-4 pt-4 md:px-6 md:pb-2 md:pt-6' : 'p-4 md:p-6'
        }`}
        aria-expanded={open}
        aria-controls={answerId}
      >
        <span className="min-w-0 flex-1 font-heading text-[15px] font-bold leading-[17px] text-card-heading md:text-base md:leading-6">
          {item.heading}
        </span>
        <ChevronIcon open={open} className="size-4 shrink-0 text-[#5A7344]" />
      </button>

      <div
        id={answerId}
        className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        inert={!open ? true : undefined}
        aria-hidden={open ? undefined : true}
      >
        <div className="overflow-hidden">
          {item.description &&
          Array.isArray(item.description) &&
          item.description.length > 0 ? (
            <div className="px-4 pb-4 pt-2.5 md:px-6 md:pb-6 md:pt-2">
              <RichText
                content={item.description}
                className="flex flex-col gap-2 font-body text-sm font-normal leading-[150%] text-text-primary md:text-base md:leading-6"
              />
            </div>
          ) : null}
        </div>
      </div>
    </FadeUp>
  )
}

function FaqGroup({ group }: { group: TeaserFaqGroupType }) {
  const items = (group.faqItems ?? []).filter((item) => item.heading?.trim())
  // Layout A → card accordion; Layout B → list accordion
  // Note: local `useCards` is inverted from CMS layout naming for historical reasons.
  const useCards = !isLayoutB(group.layout)
  const [openId, setOpenId] = useState<number | null>(
    useCards && items[0] ? items[0].id : null,
  )

  if (group.isVisible === false) return null
  if (!group.heading && !group.subHeading && items.length === 0) return null

  const configuration = group.configuration
  const contentAlignment =
    configuration?.contentAlignment ?? (useCards ? 'Left' : 'Center')
  const headerAlignClass = getHeaderAlignmentClass(contentAlignment)
  const backgroundColor = configuration?.backgroundColor?.trim() || undefined
  const backgroundImage =
    group.addBackgroundImage && group.image?.url
      ? getStrapiMedia(group.image.url)
      : null
  const backgroundPattern =
    configuration?.addBackgroundPattern && configuration.backgroundPattern?.url
      ? getStrapiMedia(configuration.backgroundPattern.url)
      : null
  const headingId = `teaser-faq-heading-${group.id}`

  const toggleItem = (id: number) => {
    setOpenId((current) => (current === id ? null : id))
  }

  if (useCards) {
    return (
      <section
        data-fig="teaser-faq"
        data-layout={group.layout ?? 'Layout A'}
        className={`relative overflow-hidden px-6 py-12 md:px-spacing-section-x md:py-spacing-section-y${
          backgroundColor ? '' : ' bg-surface-white'
        }`}
        aria-labelledby={group.heading ? headingId : undefined}
        aria-label={group.heading ? undefined : 'Frequently asked questions'}
        style={backgroundColor ? { backgroundColor } : undefined}
      >
        <BackgroundLayers image={backgroundImage} pattern={backgroundPattern} />

        <div className="relative z-10 mx-auto flex w-full max-w-width-content flex-col gap-6 md:flex-row md:items-start md:gap-8">
          {(group.heading || group.subHeading) && (
            <div
              className={`flex w-full flex-col gap-2 md:min-w-0 md:flex-1 md:gap-6 ${headerAlignClass}`}
            >
              {group.heading && (
                <FadeUp
                  as="h2"
                  id={headingId}
                  data-fig={`teaser-faq-group-${group.id}`}
                  className="font-heading text-[30px] font-bold leading-[120%] text-ink md:text-[48px] md:leading-[56px] md:text-forest"
                  delay={0}
                >
                  {group.heading}
                </FadeUp>
              )}
              {group.subHeading && (
                <FadeUp
                  as="p"
                  data-fig={`teaser-faq-subheading-${group.id}`}
                  className="font-body text-sm font-normal leading-[18px] text-text-primary md:text-base md:leading-6"
                  delay={50}
                >
                  {group.subHeading}
                </FadeUp>
              )}
            </div>
          )}

          {items.length > 0 && (
            <div className="flex w-full flex-col gap-3 md:min-w-0 md:flex-1 md:gap-4">
              {items.map((item, index) => (
                <FaqCardItem
                  key={item.id}
                  item={item}
                  open={openId === item.id}
                  delay={100 + index * 80}
                  onToggle={() => toggleItem(item.id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    )
  }

  return (
    <section
      data-fig="teaser-faq"
      data-layout={group.layout ?? 'Layout B'}
      className={`relative overflow-hidden px-6 py-12 md:px-spacing-section-x md:py-20${
        backgroundColor ? '' : ' bg-white'
      }`}
      aria-labelledby={group.heading ? headingId : undefined}
      aria-label={group.heading ? undefined : 'Frequently asked questions'}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <BackgroundLayers image={backgroundImage} pattern={backgroundPattern} />

      {/* Section: gap 32 mobile / 50 desktop */}
      <div className="relative z-10 mx-auto flex w-full max-w-width-content flex-col items-center gap-8 md:gap-[50px]">
        {(group.heading || group.subHeading) && (
          /* Header: gap 20 mobile / 8 desktop, max 715 */
          <div
            className={`flex w-full max-w-[715px] flex-col gap-5 md:gap-2 ${headerAlignClass}`}
          >
            {group.heading && (
              <FadeUp
                as="h2"
                id={headingId}
                data-fig={`teaser-faq-group-${group.id}`}
                className="m-0 font-heading text-[28px] font-bold leading-8 text-[#1C1C1A] md:text-[48px] md:leading-[56px] md:text-[#1E1E1E]"
                delay={0}
              >
                {group.heading}
              </FadeUp>
            )}
            {group.subHeading && (
              <FadeUp
                as="p"
                data-fig={`teaser-faq-subheading-${group.id}`}
                className="m-0 font-body text-[15px] font-normal leading-[22px] text-text-primary md:text-[20px] md:leading-[160%]"
                delay={50}
              >
                {group.subHeading}
              </FadeUp>
            )}
          </div>
        )}

        {items.length > 0 && (
          /*
            Mobile: item has pb-16 + border-bottom; list gap 16 between items.
            Desktop: Q/A and #E0E0E0 lines are separate flex children; list gap 24.
          */
          <div className="flex w-full flex-col gap-4 self-stretch md:gap-6">
            {items.map((item, index) => {
              const isLast = index === items.length - 1
              return (
                <Fragment key={item.id}>
                  <FaqAccordionItem
                    item={item}
                    open={openId === item.id}
                    delay={100 + index * 100}
                    onToggle={() => toggleItem(item.id)}
                    className="box-border border-b border-[#CED8C6] pb-4 md:border-0 md:pb-0"
                  />
                  {!isLast ? (
                    <div
                      aria-hidden
                      className="hidden h-0 w-full border-b border-[#E0E0E0] md:block"
                    />
                  ) : null}
                </Fragment>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

export default function TeaserFaq({ data }: Props) {
  if (!data || data.length === 0) return null

  return (
    <>
      {data.map((group) => (
        <FaqGroup key={group.id} group={group} />
      ))}
    </>
  )
}
